import React, { useEffect, useState } from 'react';
import { MapPin, AlertCircle } from 'lucide-react';
import {
    getInsuranceClinicAvailability,
    getInsuranceClinicList
} from '../../Services/APIs/patient';
// Wide horizontal wordmark (392x128) - closest match to the design's banner logo
// aspect ratio, so it stays legible at the 34px nav height.
import toothlensLogo from '../../assets/illustrations/Toothlens_logo.png';
import './networkLocator.css';

// Public coverage-lookup page (route: /network-locator).
// Intentionally unauthenticated - do NOT add the localStorage session check that
// MyBookings/PlumIframeLink use. Only a 2-clinic preview is shown; the real total
// comes from the availability API so prospects can gauge coverage before activating.
const PREVIEW_LIMIT = 2;
const REFER_DENTIST_URL = 'https://tally.so/r/XxD8ye';

// The "Testing" clinic is a test record returned by the availability API.
// There is no logged-in user here, so it is always hidden (mirrors
// filterDemoClinics in PlumAppointment, minus the demo-login escape hatch).
const filterDemoClinics = (list) =>
    list.filter(c => (c.name || '').trim().toLowerCase() !== 'testing');

const NetworkLocator = () => {
    // Search inputs
    const [pincode, setPincode] = useState('');
    const [city, setCity] = useState('');

    // Results
    const [clinics, setClinics] = useState([]);
    const [totalClinics, setTotalClinics] = useState(0);
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState('');
    const [inputError, setInputError] = useState('');

    // What was actually searched (drives the summary line)
    const [searchedLabel, setSearchedLabel] = useState('');
    const [searchedByPin, setSearchedByPin] = useState(false);

    // City dropdown
    const [availableCities, setAvailableCities] = useState([]);
    const [isLoadingCities, setIsLoadingCities] = useState(false);

    // Fetch the live city list so the dropdown always matches real coverage.
    // Same normalisation as PlumAppointment: dedupe case-insensitively,
    // title-case each word, sort. Failures are silent - the pincode search
    // still works without a city list.
    useEffect(() => {
        const fetchCities = async () => {
            setIsLoadingCities(true);
            try {
                const response = await getInsuranceClinicList();
                const body = response.data;

                // The portal host returns { data: ["Chennai", ...] }. Accept the
                // other shapes the annotation host might use rather than silently
                // rendering an empty dropdown if it differs.
                const raw =
                    (Array.isArray(body?.data) && body.data) ||
                    (Array.isArray(body?.data?.cities) && body.data.cities) ||
                    (Array.isArray(body?.cities) && body.cities) ||
                    (Array.isArray(body) && body) ||
                    [];

                const cityMap = new Map();
                raw.forEach(entry => {
                    // Tolerate both plain strings and { city } / { name } objects.
                    const cityName = typeof entry === 'string' ? entry : (entry?.city || entry?.name);
                    if (cityName && String(cityName).trim() !== '') {
                        const key = String(cityName).toLowerCase().trim();
                        if (!cityMap.has(key)) {
                            const properCity = String(cityName)
                                .trim()
                                .toLowerCase()
                                .split(' ')
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ');
                            cityMap.set(key, properCity);
                        }
                    }
                });
                setAvailableCities(Array.from(cityMap.values()).sort());
            } catch (error) {
                // Non-fatal: leave the dropdown empty, pincode search still works.
            } finally {
                setIsLoadingCities(false);
            }
        };
        fetchCities();
    }, []);

    const runSearch = async ({ pin, cityName }) => {
        setIsLoading(true);
        setApiError('');
        setInputError('');
        setHasSearched(true);
        setClinics([]);
        setTotalClinics(0);
        setSearchedLabel(pin || cityName);
        setSearchedByPin(Boolean(pin));

        // NOTE: the availability API currently ignores `page`/`pageSize` and returns
        // every matching clinic (verified: Bengaluru -> 120 records). They are sent
        // anyway to match the existing appointment-flow call and to stay correct if
        // pagination is implemented server-side later. `data.total` is the genuine
        // full count either way; the 2-card cap is applied client-side below.
        const payload = { page: 1, pageSize: 10 };
        if (pin) payload.pincode = pin;
        if (cityName) payload.city = cityName;

        try {
            const response = await getInsuranceClinicAvailability(payload);
            const clinicsData = response.data?.data?.clinics;

            // An empty array is a valid "no coverage" answer, not a failure - it
            // flows through the mapping below to totalClinics = 0, which renders
            // the not-found state.
            if (Array.isArray(clinicsData)) {
                const apiTotal = response.data?.data?.total;

                const mappedClinics = clinicsData.map(clinic => ({
                    id: clinic._id || clinic.id,
                    name: clinic.name,
                    doctor: clinic.doctor_name,
                    address: clinic.clinic_address || clinic.address,
                    address_2: clinic.locality || clinic.address_2,
                    city: clinic.city,
                    state: clinic.state,
                    pincode: clinic.pincode
                }));

                const visibleClinics = filterDemoClinics(mappedClinics);
                const hiddenOnThisPage = mappedClinics.length - visibleClinics.length;

                // Strictly the first two records the API returns. Chains legitimately
                // run several branches under one name (641604 returns two "Dhilip Dental
                // Clinic" records in different localities), so two cards can show the
                // same name - that is accepted, the card count always matches the design.
                const previewClinics = visibleClinics.slice(0, PREVIEW_LIMIT);

                setClinics(previewClinics);
                setTotalClinics(
                    Math.max((apiTotal || visibleClinics.length) - hiddenOnThisPage, previewClinics.length)
                );
            } else {
                setApiError('Failed to load clinics. Please try again.');
            }
        } catch (error) {
            // The API answers "no clinics here" with HTTP 404
            // ({ code: 404, status: 'NOT_FOUND', data: { total: 0, clinics: [] } }),
            // which axios rejects. That is not an error state - fall through to
            // the not-found card so we can still capture the dentist request.
            if (error.response?.status === 404) {
                setClinics([]);
                setTotalClinics(0);
            } else {
                setApiError('Failed to load clinics. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const searchByPin = () => {
        const trimmed = pincode.trim();
        if (!/^\d{6}$/.test(trimmed)) {
            setInputError('Please enter a valid 6-digit PIN code.');
            return;
        }
        setCity('');
        runSearch({ pin: trimmed });
    };

    const searchByCity = () => {
        if (!city) {
            setInputError('Please select a city.');
            return;
        }
        setPincode('');
        runSearch({ cityName: city });
    };

    const retrySearch = () => {
        if (searchedByPin) {
            runSearch({ pin: searchedLabel });
        } else if (searchedLabel) {
            runSearch({ cityName: searchedLabel });
        }
    };

    const clearFilters = () => {
        setPincode('');
        setCity('');
        setClinics([]);
        setTotalClinics(0);
        setHasSearched(false);
        setApiError('');
        setInputError('');
        setSearchedLabel('');
        setSearchedByPin(false);
    };

    const renderResultsBody = () => {
        if (isLoading) {
            return (
                <div className="nl-skeleton">
                    <div className="nl-skeleton-bar" style={{ width: '68%' }} />
                    <div className="nl-skeleton-cards">
                        <div className="nl-skeleton-card" />
                        <div className="nl-skeleton-card" />
                    </div>
                </div>
            );
        }

        if (apiError) {
            return (
                <div className="nl-error">
                    <div className="nl-error-icon">
                        <AlertCircle style={{ width: 24, height: 24, color: 'var(--red)' }} />
                    </div>
                    <h4>Something went wrong</h4>
                    <p>{apiError}</p>
                    <button type="button" className="btn btn-retry" onClick={retrySearch}>
                        Try again
                    </button>
                </div>
            );
        }

        if (clinics.length > 0) {
            return (
                <>
                    <div className="result-row">
                        <p className="result-summary">
                            Great news! We have <strong className="mono">{totalClinics}</strong>{' '}
                            network {totalClinics === 1 ? 'clinic' : 'clinics'} serving{' '}
                            <strong className="mono">{searchedLabel}</strong>.
                        </p>
                        <button type="button" className="btn-clear" onClick={clearFilters}>
                            Clear filter
                        </button>
                    </div>

                    <div className="clinic-grid">
                        {clinics.map((clinic, index) => (
                            <div className="clinic-card" key={clinic.id || `${clinic.name}-${index}`}>
                                <div className="eyebrow-sm">Network clinic</div>
                                <div className="name">{clinic.name}</div>
                                <div className="tag">
                                    Cashless treatment &middot; Details unlock after activation
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="more-strip">
                        <span className="left">Unlock full list after plan activation</span>
                    </div>

                    <p className="privacy-note">
                        To protect the quality of our network and keep the experience fair for
                        members, we show only a short preview of nearby clinics before activation.
                        The complete directory is available exclusively to active members.
                    </p>

                    <div className="unlock-panel">
                        <span className="label">After activation</span>
                        <h4>Unlock the complete network</h4>
                        <ul className="unlock-list">
                            <li><span className="idx">01</span>Complete list of nearby network clinics</li>
                            <li><span className="idx">02</span>Clinic addresses and contact details</li>
                            <li><span className="idx">03</span>Online appointment booking</li>
                            <li><span className="idx">04</span>Cashless treatment eligibility</li>
                            <li><span className="idx">05</span>Nearby clinic recommendations</li>
                            <li><span className="idx">06</span>Member support for appointments</li>
                        </ul>
                    </div>
                </>
            );
        }

        // No coverage found. Message only - the referral prompts live in the
        // always-visible "Know a dentist we should add?" card further down the page.
        return (
            <>
                <div className="result-row">
                    <p className="result-summary">
                        No network clinics found for{' '}
                        <strong className="mono">{searchedLabel}</strong> yet.
                    </p>
                    <button type="button" className="btn-clear" onClick={clearFilters}>
                        Clear filter
                    </button>
                </div>

                <div className="nl-empty">
                    <div className="nl-empty-icon">
                        <MapPin style={{ width: 24, height: 24 }} />
                    </div>
                    <h4>We're adding more clinics in your area</h4>
                </div>
            </>
        );
    };

    return (
        <div className="nl-root">
            <nav className="nav">
                <img
                    className="brand-logo"
                    src={toothlensLogo}
                    alt="Toothlens — Smarter Dental Care. Simplified."
                />
                <div className="nav-pill">Dental Network Locator</div>
            </nav>

            <section className="hero">
                <div className="hero-bg" />
                <div className="wrap">
                    <div className="hero-grid">
                        <div>
                            <div className="eyebrow">Cashless dental care, near you</div>
                            <h1>
                                Find a <span>cashless</span>
                                <br />
                                dental clinic near you
                            </h1>
                            <p className="lede">
                                Enter your PIN code or city to check how many Toothlens network
                                dentists are already covering your area — before you activate a plan.
                            </p>
                            <div className="stat-row">
                                <div className="stat">
                                    <span className="num mono">2,000+</span>
                                    <span className="stat-label">Network dentists</span>
                                </div>
                                <div className="stat">
                                    <span className="num mono">75+</span>
                                    <span className="stat-label">Cities covered</span>
                                </div>
                                <div className="stat">
                                    <span className="num mono">8,000+</span>
                                    <span className="stat-label">Serviceable pincodes</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="search-section">
                <div className="wrap">
                    <div className="search-card">
                        <span className="label">Coverage lookup</span>
                        <h3>Check availability</h3>
                        <p className="search-sub">
                            Search by PIN code or city — results update instantly.
                        </p>

                        <div className="search-form">
                            <div className="field">
                                <label htmlFor="nl-pin">PIN code</label>
                                <input
                                    id="nl-pin"
                                    className="mono"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="e.g. 641604"
                                    autoComplete="off"
                                    value={pincode}
                                    onChange={(e) => {
                                        setPincode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                        setInputError('');
                                    }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') searchByPin(); }}
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={searchByPin}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Checking…' : 'Check availability'}
                            </button>
                        </div>

                        <div className="or-divider">OR SELECT A CITY</div>

                        <div className="search-form">
                            <div className="field">
                                <label htmlFor="nl-city">City</label>
                                <select
                                    id="nl-city"
                                    value={city}
                                    onChange={(e) => {
                                        setCity(e.target.value);
                                        setInputError('');
                                    }}
                                >
                                    <option value="">
                                        {isLoadingCities ? 'Loading cities…' : 'Select a city…'}
                                    </option>
                                    {availableCities.map(cityName => (
                                        <option key={cityName} value={cityName}>{cityName}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={searchByCity}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Checking…' : 'Check availability'}
                            </button>
                        </div>

                        {inputError && <div className="input-error">{inputError}</div>}

                        {hasSearched && <div className="results">{renderResultsBody()}</div>}
                    </div>

                    <div className="request-card">
                        <div className="txt">
                            <span className="label">Missing a clinic</span>
                            <h3>Know a dentist we should add?</h3>
                            <p>
                                We're continuously expanding our network. Refer your dentist and
                                we'll reach out about joining.
                            </p>
                        </div>
                        <a
                            className="btn btn-outline"
                            href={REFER_DENTIST_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Refer your dentist
                        </a>
                    </div>
                </div>
            </section>

            <section className="section section-alt">
                <div className="wrap">
                    <div className="section-head">
                        <span className="label">Member benefits</span>
                        <h2>The value of staying in-network</h2>
                        <p>
                            Every clinic has agreed to the same standards and prices, so the savings
                            compound with every visit.
                        </p>
                    </div>
                    <div className="why-grid">
                        <div className="why-card">
                            <span className="idx">01</span>
                            <h3>2,000+ verified dentists</h3>
                            <p>Every clinic is vetted before joining the network.</p>
                        </div>
                        <div className="why-card">
                            <span className="idx">02</span>
                            <h3>Availability across 75+ cities</h3>
                            <p>From metros to growing towns, care is never far away.</p>
                        </div>
                        <div className="why-card">
                            <span className="idx">03</span>
                            <h3>Cashless dental treatment</h3>
                            <p>Walk in, get treated, walk out — no upfront payment.</p>
                        </div>
                        <div className="why-card">
                            <span className="idx">04</span>
                            <h3>Transparent, standardized costs</h3>
                            <p>The same procedure costs the same, clinic to clinic.</p>
                        </div>
                        <div className="why-card">
                            <span className="idx">05</span>
                            <h3>Fast digital approvals</h3>
                            <p>Treatment approvals happen in minutes, not days.</p>
                        </div>
                        <div className="why-card">
                            <span className="idx">06</span>
                            <h3>150+ procedures</h3>
                            <p>From routine cleanings to complex restorative work.</p>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="nl-footer">
                <div className="wrap">
                    <span className="label">Toothlens Dental Network</span>
                    <div className="brand">Cashless care, verified clinics.</div>
                    <p>
                        Your complete clinic directory, contact details, appointment booking, and
                        cashless eligibility become available once your dental plan is successfully
                        activated.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default NetworkLocator;
